import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { 
  getCategories, 
  createCategory, 
  updateCategory, 
  deleteCategory,
  TRANSACTION_TYPES,
  TYPE_COLORS
} from '../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  FolderTree,
  Briefcase,
  Coins,
  Home,
  Zap,
  Flame,
  Droplet,
  ShoppingCart,
  Tv,
  Sparkles,
  Plane,
  Wifi,
  Shield,
  Bus,
  Gamepad2,
  Wallet,
  Sofa,
  Gift,
  Landmark,
  ShieldCheck,
  Palmtree,
  PiggyBank,
  GraduationCap,
  Building2,
  Hammer,
  Car,
  Building,
  CreditCard,
  Banknote,
  List,
  Columns3,
  GripVertical,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

const CATEGORY_ICONS = {
  'briefcase': Briefcase,
  'coins': Coins,
  'home': Home,
  'zap': Zap,
  'flame': Flame,
  'droplet': Droplet,
  'shopping-cart': ShoppingCart,
  'tv': Tv,
  'sparkles': Sparkles,
  'plane': Plane,
  'wifi': Wifi,
  'shield': Shield,
  'bus': Bus,
  'gamepad-2': Gamepad2,
  'wallet': Wallet,
  'sofa': Sofa,
  'gift': Gift,
  'landmark': Landmark,
  'shield-check': ShieldCheck,
  'palmtree': Palmtree,
  'piggy-bank': PiggyBank,
  'graduation-cap': GraduationCap,
  'building-2': Building2,
  'hammer': Hammer,
  'car': Car,
  'building': Building,
  'credit-card': CreditCard,
  'banknote': Banknote
};

const TYPE_META_LITE = {
  REDDITO:  { label: 'Reddito',   accent: 'border-emerald-300 dark:border-emerald-900/50', headerBg: 'bg-emerald-50 dark:bg-emerald-900/20', text: 'text-emerald-700 dark:text-emerald-300' },
  SPESE:    { label: 'Spese',     accent: 'border-red-300 dark:border-red-900/50',         headerBg: 'bg-red-50 dark:bg-red-900/20',         text: 'text-red-700 dark:text-red-300' },
  RISPARMI: { label: 'Risparmi',  accent: 'border-blue-300 dark:border-blue-900/50',       headerBg: 'bg-blue-50 dark:bg-blue-900/20',       text: 'text-blue-700 dark:text-blue-300' },
  DEBITI:   { label: 'Debiti',    accent: 'border-orange-300 dark:border-orange-900/50',   headerBg: 'bg-orange-50 dark:bg-orange-900/20',   text: 'text-orange-700 dark:text-orange-300' },
};

const CategoriesPage = () => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('REDDITO');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('cat:viewMode') || 'list');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Drag&drop
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  // Form
  const [formName, setFormName] = useState('');
  const [formType, setFormType] = useState('SPESE');
  const [formIcon, setFormIcon] = useState('coins');

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem('cat:viewMode', mode);
  };

  const handleDrop = async (targetType) => {
    if (!draggingId) return;
    const cat = categories.find((c) => c.id === draggingId);
    setDraggingId(null);
    setDropTarget(null);
    if (!cat || cat.type === targetType) return;
    // Optimistic update
    setCategories((prev) => prev.map((c) => (c.id === cat.id ? { ...c, type: targetType } : c)));
    try {
      await updateCategory(cat.id, { name: cat.name, type: targetType, icon: cat.icon });
      toast.success(`"${cat.name}" → ${TYPE_META_LITE[targetType].label}`);
    } catch (e) {
      toast.error('Errore spostamento');
      loadData();
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingCategory(null);
    setFormName('');
    setFormType(activeTab);
    setFormIcon('coins');
    setDialogOpen(true);
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormType(category.type);
    setFormIcon(category.icon || 'coins');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formName) {
      toast.error('Inserisci un nome');
      return;
    }

    const data = {
      name: formName,
      type: formType,
      icon: formIcon
    };

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, data);
        toast.success('Categoria aggiornata');
      } else {
        await createCategory(data);
        toast.success('Categoria creata');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      await deleteCategory(categoryToDelete.id);
      toast.success('Categoria eliminata');
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Errore durante l\'eliminazione. Potrebbe essere in uso.');
    }
  };

  const getCategoriesByType = (type) => categories.filter(c => c.type === type);

  return (
    <div className="space-y-6 animate-in" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Categorie</h1>
          <p className="text-muted-foreground">
            Gestisci le categorie · {viewMode === 'kanban' ? 'trascina una card fra le colonne per cambiarne il tipo' : 'organizza le transazioni'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border bg-muted/30 p-0.5">
            <button
              onClick={() => switchView('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === 'list' ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-list-btn"
            >
              <List className="w-4 h-4" /> Lista
            </button>
            <button
              onClick={() => switchView('kanban')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1.5 ${
                viewMode === 'kanban' ? 'bg-card shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="view-kanban-btn"
            >
              <Columns3 className="w-4 h-4" /> Kanban
            </button>
          </div>
          <Button onClick={openAddDialog} data-testid="add-category-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuova
          </Button>
        </div>
      </div>

      {viewMode === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kanban-board">
          {TRANSACTION_TYPES.map((t) => {
            const meta = TYPE_META_LITE[t.value];
            const list = getCategoriesByType(t.value);
            const isTarget = dropTarget === t.value;
            return (
              <div
                key={t.value}
                onDragOver={(e) => { e.preventDefault(); setDropTarget(t.value); }}
                onDragLeave={() => setDropTarget((prev) => (prev === t.value ? null : prev))}
                onDrop={() => handleDrop(t.value)}
                className={`rounded-xl border-2 ${meta.accent} transition-all ${
                  isTarget && draggingId ? 'ring-2 ring-primary/40 scale-[1.02]' : ''
                }`}
                data-testid={`kanban-col-${t.value}`}
              >
                <div className={`${meta.headerBg} px-4 py-3 rounded-t-lg flex items-center justify-between`}>
                  <div>
                    <h3 className={`text-xs uppercase tracking-wider font-semibold ${meta.text}`}>{meta.label}</h3>
                    <p className="text-[11px] text-muted-foreground">{list.length} categorie</p>
                  </div>
                  <Button
                    size="icon" variant="ghost"
                    onClick={() => { setActiveTab(t.value); openAddDialog(); }}
                    className="w-7 h-7"
                    data-testid={`kanban-add-${t.value}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="p-3 space-y-2 min-h-[140px] max-h-[480px] overflow-y-auto">
                  {list.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6 italic">Trascina qui o aggiungi una nuova categoria</p>
                  ) : (
                    list.map((category) => {
                      const IconComponent = CATEGORY_ICONS[category.icon] || Coins;
                      const isDragging = draggingId === category.id;
                      return (
                        <div
                          key={category.id}
                          draggable
                          onDragStart={() => setDraggingId(category.id)}
                          onDragEnd={() => { setDraggingId(null); setDropTarget(null); }}
                          className={`group rounded-lg border bg-card p-2.5 flex items-center gap-2 cursor-grab active:cursor-grabbing transition-all ${
                            isDragging ? 'opacity-40 scale-95' : 'hover:shadow-md hover:-translate-y-0.5'
                          }`}
                          data-testid={`kanban-card-${category.id}`}
                        >
                          <GripVertical className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
                          <div className="w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0">
                            <IconComponent className={`w-4 h-4 ${meta.text}`} />
                          </div>
                          <span className="font-medium text-sm flex-1 truncate">{category.name}</span>
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                            <Button size="icon" variant="ghost" className="w-7 h-7" onClick={() => openEditDialog(category)}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="w-7 h-7"
                              onClick={() => { setCategoryToDelete(category); setDeleteDialogOpen(true); }}>
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <TabsList className="grid w-full grid-cols-4">
              {TRANSACTION_TYPES.map(t => (
                <TabsTrigger key={t.value} value={t.value} data-testid={`cat-tab-${t.value}`}>
                  {t.label} ({getCategoriesByType(t.value).length})
                </TabsTrigger>
              ))}
            </TabsList>
          </CardHeader>
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
            ) : (
              <>
                {TRANSACTION_TYPES.map(type => (
                  <TabsContent key={type.value} value={type.value} className="mt-0">
                    {getCategoriesByType(type.value).length === 0 ? (
                      <div className="py-12 text-center">
                        <FolderTree className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">Nessuna categoria</h3>
                        <p className="text-muted-foreground mb-4">
                          Aggiungi categorie per {type.label.toLowerCase()}
                        </p>
                        <Button onClick={openAddDialog}>
                          <Plus className="w-4 h-4 mr-2" /> Aggiungi categoria
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {getCategoriesByType(type.value).map((category) => {
                          const IconComponent = CATEGORY_ICONS[category.icon] || Coins;
                          return (
                            <div 
                              key={category.id}
                              className={`p-4 rounded-lg border ${TYPE_COLORS[category.type]?.bg} flex items-center justify-between`}
                              data-testid={`category-card-${category.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg bg-background/80 flex items-center justify-center`}>
                                  <IconComponent className={`w-5 h-5 ${TYPE_COLORS[category.type]?.text}`} />
                                </div>
                                <span className="font-medium">{category.name}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEditDialog(category)}
                                  data-testid={`edit-category-${category.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => { setCategoryToDelete(category); setDeleteDialogOpen(true); }}
                                  data-testid={`delete-category-${category.id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                ))}
              </>
            )}
          </CardContent>
        </Tabs>
      </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="es. Abbonamento palestra"
                required
                data-testid="category-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={formType} onValueChange={setFormType} disabled={!!editingCategory}>
                <SelectTrigger data-testid="category-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSACTION_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Icona</Label>
              <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                {Object.entries(CATEGORY_ICONS).map(([key, Icon]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormIcon(key)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      formIcon === key 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                    data-testid={`category-icon-${key}`}
                  >
                    <Icon className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" data-testid="save-category-btn">
                {editingCategory ? 'Salva Modifiche' : 'Crea Categoria'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="delete-category-dialog">
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
          </DialogHeader>
          <p>
            Sei sicuro di voler eliminare la categoria "{categoryToDelete?.name}"?
          </p>
          <p className="text-sm text-muted-foreground">
            Le transazioni associate a questa categoria non verranno eliminate, ma perderanno la categoria.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-category-btn">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriesPage;
